import { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FilterOperator, FilterGroup } from '@/lib/utils';
import { FilterBar } from '@/components/filters/FilterBar';
import { Pagination } from '@/components/navigation/Pagination';
import {
  flexRender,
  getCoreRowModel,
  SortingState,
  Updater,
  useReactTable,
  VisibilityState,
  ColumnDef,
} from '@tanstack/react-table';
import { FilterFieldConfig, FilterValues } from '@/hooks/useJsonFilters';
import { ColumnVisibilityToggle } from '@/components/table/column-visibility';
import { TableRowSkeletons } from '@/components/table/row-skeletons';
import { useFullPageTableState } from '@/hooks/useFullPageTableState';
import { Button } from '@/components/ui/button';
import { Filter } from 'lucide-react';
import { Input } from '../ui/input';

export interface FullPageTableProps<TData> {
  title: string;
  columns: ColumnDef<TData>[];
  data: TData[];
  loading?: boolean;
  error?: Error | null;

  // Pagination
  page: number;
  pageSize: number;
  totalCount?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];

  // Filtering - legacy config-based approach
  filterConfig?: FilterFieldConfig[];
  /** Default values for filters (e.g., from URL params) */
  defaultValues?: FilterValues;
  onFiltersChange?: (params: {
    filterOperator: FilterOperator;
    filterFilterGroups: FilterGroup[];
    values: Record<string, string | Date | undefined>;
  }) => void;
  /** Render prop for custom filter content (component-based approach) */
  renderFilters?: () => React.ReactNode;

  // State persistence
  stateKey: string;
  defaultColumnVisibility?: VisibilityState;
  defaultSorting?: SortingState;

  // Sorting callback (for server-side sorting)
  onSortingChange?: (sorting: SortingState) => void;

  // Custom empty state
  emptyMessage?: string;

  // UI behavior
  defaultFiltersOpen?: boolean;
  hideTopSearch?: boolean;
  disableFilterToggle?: boolean;
  
  // Onboarding
  searchDataOnboarding?: string;
  tableDataOnboarding?: string;
  paginationDataOnboarding?: string;
  columnToggleDataOnboarding?: string;
  filterButtonDataOnboarding?: string;
}

export function FullPageTable<TData>({
  title,
  columns,
  data,
  loading = false,
  error,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 100],
  filterConfig,
  defaultValues,
  onFiltersChange,
  renderFilters,
  stateKey,
  defaultColumnVisibility = {},
  defaultSorting = [],
  onSortingChange,
  emptyMessage = 'No results found',
  defaultFiltersOpen = false,
  hideTopSearch = false,
  disableFilterToggle = false,
  searchDataOnboarding,
  tableDataOnboarding,
  paginationDataOnboarding,
  columnToggleDataOnboarding,
  filterButtonDataOnboarding,
}: FullPageTableProps<TData>) {
  // Open filters sidebar if defaultValues has any non-empty values, or if defaultFiltersOpen is true
  const hasActiveFilters = defaultValues && Object.values(defaultValues).some(v => v !== undefined && v !== '');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    disableFilterToggle ? false : !(defaultFiltersOpen || hasActiveFilters)
  );

  const { columnVisibility, sorting, setColumnVisibility, setSorting } = useFullPageTableState(
    stateKey,
    defaultColumnVisibility,
    defaultSorting
  );

  const handleColumnVisibilityChange = (updater: Updater<VisibilityState>) => {
    const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newVisibility);
  };

  const handleSortingChange = (updater: Updater<SortingState>) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(newSorting);
    onSortingChange?.(newSorting);
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onSortingChange: handleSortingChange,
    state: {
      columnVisibility,
      sorting,
    },
  });

  const showFilters = filterConfig && filterConfig.length > 0;
  const showPagination = totalCount != null;

  // Find the first text filter for the search input
  const firstTextFilter = filterConfig?.find(cfg => cfg.type === 'text');
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

  // Debounce the search value
  useEffect(() => {
    if (hideTopSearch) {
      setDebouncedSearchValue('');
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchValue, hideTopSearch]);

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
  }, []);

  // Create filter params (structure without values)
  const filterParams = useMemo(() => ({
    filterOperator: FilterOperator.And,
    filterFilterGroups: debouncedSearchValue && firstTextFilter ? [{
      operator: FilterOperator.And,
      filters: [{
        field: firstTextFilter.field,
        value: debouncedSearchValue,
        action: firstTextFilter.comparator,
      }],
    }] : [],
  }), [debouncedSearchValue, firstTextFilter]);

  // Create debounced values
  const debouncedValues = useMemo(() => ({
    [firstTextFilter?.name || '']: debouncedSearchValue || undefined,
  }), [debouncedSearchValue, firstTextFilter]);

  // Apply filters when debounced values change
  useEffect(() => {
    if (onFiltersChange && !hideTopSearch) {
      onFiltersChange({ ...filterParams, values: debouncedValues });
    }
  }, [filterParams, debouncedValues, onFiltersChange, hideTopSearch]);

  if (error) {
    return (
      <div className="flex h-full flex-col">
        <div className="sticky top-14 z-20 flex items-center justify-between border-b bg-[#111] p-4">
          <h1 className="text-2xl font-semibold">{title}</h1>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-500">Error loading data</p>
            <p className="text-sm text-muted-foreground">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {title && (
        <div className="sticky top-14 z-20 flex items-center justify-between border-b bg-[#111] p-4">
          <h1 className="text-2xl font-semibold">{title}</h1>
        </div>
      )}
      <div className='p-4 border-b flex items-center gap-2 m-4 border mt-0 rounded-xl bg-card1'>
        {!hideTopSearch && (
          <Input
            isSearch
            type="text"
            placeholder={firstTextFilter ? `Search ${firstTextFilter.label.toLowerCase()}...` : "Search"}
            value={searchValue}
            onChange={handleSearchChange}
            className="input input-bordered w-full max-w-xs"
            data-onboarding={searchDataOnboarding}
          />
        )}
        <div className='flex items-center gap-2'>
          {columnToggleDataOnboarding ? (
            <div data-onboarding={columnToggleDataOnboarding}>
              <ColumnVisibilityToggle table={table} />
            </div>
          ) : (
            <ColumnVisibilityToggle table={table} />
          )}
          {showFilters && !disableFilterToggle && !sidebarCollapsed && (
            <Button
              variant="outline"
              onClick={() => setSidebarCollapsed(true)}
              data-onboarding={filterButtonDataOnboarding}
                      className=" min-w-36"
            >
              Close Filter <Filter />
            </Button>
          )}
          {showFilters && !disableFilterToggle && sidebarCollapsed && (
            <Button
              variant="outline"
              onClick={() => setSidebarCollapsed(false)}
              className=" min-w-36"
              data-onboarding={filterButtonDataOnboarding}
            >
              Open Filter<Filter />
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col-reverse md:flex-row-reverse min-h-[calc(100vh-220px)]">
        {showFilters && !sidebarCollapsed && (
          <div className=" border-[#2E3835] md:w-[440px]  border-l border-t rounded-tl-xl overflow-hidden bg-card1">
            <div className="flex items-center justify-between">
              
           
            </div>
            {renderFilters ? (
              renderFilters()
            ) : (
              <FilterBar 
                config={filterConfig}
                defaultValues={defaultValues}
                onFiltersChange={onFiltersChange} 
                column 
                onHide={() => setSidebarCollapsed(true)}
              />
            )}
          </div>
        )}

   

        <div className="h-full flex-1">
          <div className="relative border m-4 rounded-xl mt-0 bg-card1 overflow-hidden">
            <Table className="relative" data-onboarding={tableDataOnboarding}>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead
                          key={header.id}
                          style={{ textAlign: header.column.columnDef.meta?.align }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              {loading ? (
                <TableBody>
                  <TableRowSkeletons table={table} rowCount={pageSize} />
                </TableBody>
              ) : (
                <>
                  {table.getRowModel().rows?.length ? (
                    <>
                      <TableBody data-onboarding={tableDataOnboarding ? `${tableDataOnboarding}-body` : undefined}>
                        {table.getRowModel().rows.slice(0, 10).map((row) => (
                          <TableRow 
                            key={row.id} 
                            data-state={row.getIsSelected() && 'selected'}
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell
                                key={cell.id}
                                align={cell.column.columnDef.meta?.align}
                                style={cell.column.columnDef.meta?.style}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                      {table.getRowModel().rows.length > 10 && (
                        <TableBody>
                          {table.getRowModel().rows.slice(10).map((row) => (
                            <TableRow 
                              key={row.id} 
                              data-state={row.getIsSelected() && 'selected'}
                            >
                              {row.getVisibleCells().map((cell) => (
                                <TableCell
                                  key={cell.id}
                                  align={cell.column.columnDef.meta?.align}
                                  style={cell.column.columnDef.meta?.style}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      )}
                    </>
                  ) : (
                    <TableBody>
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          {emptyMessage}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </>
              )}
            </Table>

          {showPagination && (
            <div className="border-t bg-background/50 px-4 py-3" data-onboarding={paginationDataOnboarding}>
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground min-w-0 flex-1">
                  Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalCount!)} of {totalCount} results
                </div>
                <div className="flex items-center justify-center flex-1">
                  <Pagination
                    onPageChange={onPageChange}
                    page={page}
                    totalPages={Math.ceil(totalCount! / pageSize)}
                  />
                </div>
                {onPageSizeChange && (
                  <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground min-w-0 flex-1">
                    <label htmlFor="page-size-select">Per page</label>
                    <select
                      id="page-size-select"
                      value={pageSize}
                      onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
                      className="rounded border border-gray-600 bg-transparent px-2 py-1"
                    >
                      {pageSizeOptions.map((size) => (
                        <option key={size} value={size} className="bg-gray-800">
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>

        </div>
      </div>
    </div>
  );
}